import { t } from 'i18next';
import {
  DAILY_FREQUENCY,
  IMPACT_REPORT_CATEGORY_PREFIX,
  IMPACT_REPORT_KPI_EXPECTED_ANNUAL_SUFFIX,
  IMPACT_REPORT_KPI_PREFIX,
  type ImpactReportCategory,
  type ImpactReportFrequencies,
  type ImpactType,
  MONTHLY_FREQUENCY,
  QUARTERLY_FREQUENCY,
  type Stages,
  WEEKLY_FREQUENCY,
  YEARLY_FREQUENCY,
} from '@/impactReports/impactReport.types';

export function getCustomCategoryTitle(category: ImpactReportCategory) {
  return category?.category?.name?.substring(IMPACT_REPORT_CATEGORY_PREFIX.length) ?? '';
}

export function getKPIColumnName(kpiDisplayName: string, kpiUnit: string) {
  return `${IMPACT_REPORT_KPI_PREFIX}${kpiDisplayName}_${kpiUnit}`;
}

export function getKPIDisplayName(kpiColumnName: string) {
  return kpiColumnName.substring(IMPACT_REPORT_KPI_PREFIX.length, kpiColumnName.lastIndexOf('_')) ?? '';
}

export function getKPIUnit(kpiColumnName: string) {
  if (!kpiColumnName.startsWith(IMPACT_REPORT_KPI_PREFIX)) return '';
  return kpiColumnName.substring(kpiColumnName.lastIndexOf('_') + 1) ?? '';
}

export function getKPIItemColumnName(kpiColumnName: string) {
  return `${kpiColumnName}_item`;
}

export function getKPIExpectedAnnualColumnName(kpiColumnName: string) {
  return `${kpiColumnName}${IMPACT_REPORT_KPI_EXPECTED_ANNUAL_SUFFIX}`;
}

export function isKPIExpectedAnnualColumnName(columnName: string) {
  return (
    columnName.startsWith(IMPACT_REPORT_KPI_PREFIX) && columnName.endsWith(IMPACT_REPORT_KPI_EXPECTED_ANNUAL_SUFFIX)
  );
}

export function isKPIColumnName(columnName: string) {
  return columnName.startsWith(IMPACT_REPORT_KPI_PREFIX) && !isKPIExpectedAnnualColumnName(columnName);
}

export function getKPIColumnNameFromExpectedAnnual(columnName: string) {
  if (!isKPIExpectedAnnualColumnName(columnName)) return columnName;
  return columnName.replace(IMPACT_REPORT_KPI_EXPECTED_ANNUAL_SUFFIX, '');
}

export function getStageDisplayString(stage: Stages) {
  if (!stage) return '';
  if (t(`IMPACT_REPORT.${stage.toUpperCase()}`) === `IMPACT_REPORT.${stage.toUpperCase()}`) return stage;
  return t(`IMPACT_REPORT.${stage.toUpperCase()}`);
}

export function getCategoryDisplayString(
  categoryId: string,
  categories: { value: string; label: string }[] | undefined,
  fallback = '',
) {
  const category = categories?.find((category) => category.value === categoryId);
  return category ? category.label : fallback;
}

export function formatNumberAsValue(input: number | string, unit: string, decimalPlaces?: number): string {
  const num = Number(input);
  if (!Number.isFinite(num)) return '';

  // If decimalPlaces is explicitly specified, use full formatting with that precision
  if (decimalPlaces !== undefined) {
    const formatted = num.toFixed(decimalPlaces);
    // Add comma formatting for numbers >= 1000
    if (Math.abs(num) >= 1000) {
      const [intPart, decPart] = formatted.split('.');
      const withCommas = new Intl.NumberFormat(undefined).format(parseInt(intPart, 10));
      return `${unit} ${decPart !== undefined ? `${withCommas}.${decPart}` : withCommas}`;
    }
    return `${unit} ${formatted}`;
  }

  // Otherwise, use K/M abbreviation for large numbers
  if (num >= 1_000_000) {
    return `${unit} ${(num / 1_000_000).toFixed(3)}M`;
  } else if (num >= 1_000) {
    return `${unit} ${(num / 1_000).toFixed(0)}K`;
  } else {
    return `${unit} ${new Intl.NumberFormat(undefined).format(Math.round(num))}`;
  }
}

export function getImpactTypeDisplayString(impactType: ImpactType) {
  switch (impactType) {
    case 'continuous':
      return t('IMPACT_REPORT.CONTINUOUS');
    case 'oneTime':
      return t('IMPACT_REPORT.ONE_TIME');
    case 'customCalc':
      return t('IMPACT_REPORT.CUSTOM_CALC');
    default:
      return impactType;
  }
}

export function formatFrequency(frequency: ImpactReportFrequencies) {
  switch (frequency) {
    case DAILY_FREQUENCY:
      return t('IMPACT_REPORT.FREQUENCIES.DAILY');
    case WEEKLY_FREQUENCY:
      return t('IMPACT_REPORT.FREQUENCIES.WEEKLY');
    case MONTHLY_FREQUENCY:
      return t('IMPACT_REPORT.FREQUENCIES.MONTHLY');
    case QUARTERLY_FREQUENCY:
      return t('IMPACT_REPORT.FREQUENCIES.QUARTERLY');
    case YEARLY_FREQUENCY:
      return t('IMPACT_REPORT.FREQUENCIES.YEARLY');
    default: {
      const _exhaustiveCheck: never = frequency;
      throw new Error(`Unhandled frequency case: ${_exhaustiveCheck}`);
    }
  }
}
